import {
  Input,
  Paginate,
  Switch,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { AddCircleIcon } from 'cokitchen-icons';
import React, { useState, useEffect, useCallback } from 'react';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import VendorBulkActions from './bulk-vendor-actions';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { debounce } from 'utils/functions';
import { MAIN_STORE_ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import useBrandChefsContext from 'contexts/brand-chefs.context';
import { BrandChefsMealType } from 'models/brand-chefs.model';
import MealStatusTag from 'components/base/meal-status-tag';
import { ENUM_MEAL_STATUS } from 'models/common.model';
import moment from 'moment';

const ManageMeals = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<BrandChefsMealType[]>([]);
  const [vendorsArray, setVendorsArray] = useState<BrandChefsMealType[]>([]);
  const [isFetchingVendors, setIsFetchingVendors] = useState(false);
  const [activeMealIds, setActiveMealIds] = useState<string[]>([]);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const { setItemToEdit } = useBrandChefsContext();
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  const isAllSelected = () => {
    return vendorsArray.length > 0 && vendorsArray.length === selectedItems.length;
  };
  const selectVendor = (id: string) => {
    const vendor = vendorsArray.find((vendor) => vendor.ingredient_id === id);
    if (vendor) {
      if (selectedItems.includes(vendor)) {
        setSelectedItems(selectedItems.filter((vendor) => vendor.ingredient_id !== id));
      } else {
        setSelectedItems([...selectedItems, vendor]);
      }
    }
  };
  const [showBulkActions, setShowBulkActions] = useState<boolean>(true);

  const handleRequest = (item?: BrandChefsMealType) => {
    navigate(MAIN_STORE_ROUTE_KEYS.REQUEST_MANAGE_MEALS);
    setItemToEdit(
      item
        ? [
            {
              id: item.id,
              item_type: item?.type,
              ingredient_name: item.ingredient.name,
              ingredient_id: item.ingredient.id,
              request_portion: '',
              items: []
            }
          ]
        : selectedItems?.map((_) => ({
            id: _.id,
            item_type: _?.type,
            ingredient_name: _.ingredient.name,
            ingredient_id: _.ingredient.id,
            request_portion: '',
            items: []
          }))
    );
  };

  const toggleAll = () => {
    if (isAllSelected()) {
      setSelectedItems([]);
    } else {
      setSelectedItems(vendorsArray);
    }
  };

  useEffect(() => {
    if (selectedItems.length > 1) {
      setShowBulkActions(true);
    } else {
      setShowBulkActions(false);
    }
  }, [selectedItems]);

  const fetchMeals = async (
    searchString: string,
    categoryString: string,
    current_page?: number,
    preventRealod?: boolean
  ) => {
    try {
      if (!preventRealod) {
        setIsFetchingVendors(true);
      }
      const response = await apiService.get(`cokitchen-item`, {
        params: {
          ...(searchString ? { cokitchen_item_name: searchString } : {}),
          ...(categoryString ? { category_name: categoryString } : {}),
          cokitchen_id: cokitchenId,
          page: current_page || '1'
        }
      });
      if (response) {
        setVendorsArray(response?.data?.results);
        setLastPage(Math.ceil(response?.data?.total / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching vendors'}
          type='error'
        />
      );
    } finally {
      setIsFetchingVendors(false);
    }
  };

  const debounceFetchMeals = useCallback(debounce(fetchMeals, 1000), []);

  useEffect(() => {
    setIsFetchingVendors(true);
    clearVendorsList();
    debounceFetchMeals(search, '', currentPage);
  }, [search, currentPage]);

  const clearVendorsList = () => {
    setVendorsArray([]);
    setSelectedItems([]);
  };
  const updateMealAvailability = async (id: string, isAvailable: boolean) => {
    if (activeMealIds?.includes(id)) {
      return;
    }
    try {
      setActiveMealIds((prev) => [...prev, id]);
      await apiService.post(
        isAvailable
          ? `cokitchen-item/${cokitchenId}/meals/${id}/toggle-off`
          : `cokitchen-item/${cokitchenId}/meals/${id}/toggle-on`
      );
      fetchMeals(search, '', currentPage, true);
      toast(
        <ToastMessage
          message={`Meal switch ${isAvailable ? 'off' : 'on'} successfully`}
          type='success'
        />
      );
    } catch (err: any) {
      toast(<ToastMessage message={'Error updating meal status'} type='error' />);
    } finally {
      setActiveMealIds(activeMealIds.filter((_) => _ !== id));
    }
  };
  return (
    <div className='px-2 md:px-6 mt-2 mb-10 manage-meals'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>Meals</h4>
          <p className='text-[#6F7376] text-sm'>Manage meal items availability</p>
        </div>
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <Input
            type='search'
            placeholder='Search meal item'
            className='w-[300px]'
            inputClass='h-[44px]'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            clearVendorsList();
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>
      <Table
        headers={['Meal', 'Status', '', '', '']}
        isAllSelected={isAllSelected()}
        onToggleAll={toggleAll}
        loading={isFetchingVendors}>
        {vendorsArray.map((vendor) => (
          <>
            <TableRow
              key={vendor.id}
              // canSelect
              className={`table_row  ${!Number(vendor.quantity) ? 'bg-[#F4F5F5]' : ''}`}>
              <TableData className='cursor-pointer'>
                <div
                  // onClick={() => navigate(vendor.ingredient.id.toString())}
                  onClick={() => {
                    selectVendor(vendor?.ingredient_id);
                  }}
                  className='flex justify-start items-center gap-3'>
                  <Input
                    type='checkbox'
                    className='mr-2'
                    checkColor='gray'
                    checked={selectedItems.includes(vendor)}
                    onChange={(e) => {
                      console.log(e);
                    }}
                  />
                  <span className='text-base font-semibold'>{vendor?.ingredient?.name}</span>
                </div>
              </TableData>

              <TableData>
                <MealStatusTag
                  status={
                    Number(vendor.quantity) < 1
                      ? ENUM_MEAL_STATUS.SOLD_OUT
                      : ENUM_MEAL_STATUS.AVAILABLE
                  }
                />
              </TableData>

              <TableData>
                <Switch
                  active={vendor.available}
                  disabled={activeMealIds.includes(vendor.id)}
                  onChange={() => {
                    updateMealAvailability(vendor.id, vendor.available);
                  }}
                />
              </TableData>

              <TableData>
                {vendor?.estimated_available_time ? (
                  <span className='text-sm'>
                    will be available in {moment(vendor?.estimated_available_time || '').fromNow()}
                  </span>
                ) : (
                  ''
                )}
              </TableData>
              <TableData>
                <button
                  onClick={() => {
                    handleRequest(vendor);
                  }}
                  className='flex justify-center items-center gap-1 bg-white px-3 py-1 rounded-full'>
                  <AddCircleIcon className='w-[18px] h-[18px]' />
                  <span className='text-gray-12 font-medium'>Request</span>
                </button>
              </TableData>
            </TableRow>
          </>
        ))}
      </Table>

      {selectedItems.length !== 0 && (
        <VendorBulkActions
          item={selectedItems}
          requestFunc={handleRequest}
          showBulkActions={showBulkActions}
          setShowBulkActions={setShowBulkActions}
          setSelectedItems={setSelectedItems}
        />
      )}
    </div>
  );
};

export default ManageMeals;
